import { Link, useNavigate } from "react-router-dom";
import { useLocale } from "@/i18n/LocaleProvider";
import { goToLoginAndReturn } from "@/auth/returnTo";
import { useAuth } from "@/auth/AuthProvider";
import { UserMenu } from "@/components/UserMenu";
import { BrandMark } from "@/brand/BrandMark";

/* Site header, transcribed from /legacy.
 *
 * Structure and order are the legacy's: logo lockup, then the nav (Buy, Rent,
 * Sell, Agents, Pricing, AI Advisor), then EN/AR toggle, sign-in link and the
 * amber Subscribe button.
 *
 * W4: every one of these was a <span> -- nav that looked live and did
 * nothing. They are real links now:
 *   Buy / Rent -> pre-filtered results
 *   Sell       -> the posting flow (panel when signed in, login when not)
 *   Pricing    -> /pricing;  Subscribe -> /pricing
 *   AI         -> the assistant in the panel, or login first
 * Agents stays dimmed and inert with its "Coming soon" title, as in /legacy:
 * there is no agents directory and no public route that could list one, so a
 * link would be a door onto nothing. Marked rather than pretended.
 *
 * The logo keeps the legacy lockup geometry -- a 32px teal rounded tile -- with
 * the SouqAkar mark inside it in place of the old amber square, per the brand
 * rules. The wordmark is Latin in both locales.
 */
export function Header() {
  const { t, locale, setLocale, ar } = useLocale();
  const { profile } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sq-header">
      {/* The logo carries its own wordmark, so nothing beside it repeats the
          name (ruling i-2: the original logo, unaltered). The two-script
          stacked lockup is gone with it -- it existed to say in text what the
          logo already says in the artwork. */}
      <Link to="/" className="sq-logo" aria-label="SouqAkar">
        <BrandMark width={ar ? 132 : 132} title="SouqAkar" />
      </Link>

      <nav className="sq-nav" aria-label={t.nav.buy}>
        <Link className="sq-nav__link" to="/search?for=Sale">{t.nav.buy}</Link>
        <Link className="sq-nav__link" to="/search?for=Rent">{t.nav.rent}</Link>
        {/* Posting lives in the panel; signed-out visitors sign in first and
            are returned here by the login flow's return path. */}
        <Link className="sq-nav__link" to={profile ? "/account#listings" : "/login"}>
          {t.nav.sell}
        </Link>
        <span className="sq-nav__link sq-nav__link--soon" title={t.nav.comingSoon}>
          {t.nav.agents}
        </span>
        <Link className="sq-nav__link" to="/pricing">{t.nav.pricing}</Link>
        <Link className="sq-nav__link sq-nav__link--ai" to={profile ? "/account#ai" : "/login"}>
          ✨ {t.nav.ai}
        </Link>
      </nav>

      <div className="sq-header__right">
        <div className="sq-langtoggle">
          <button
            type="button"
            className="sq-langtoggle__btn"
            aria-pressed={locale === "en"}
            onClick={() => setLocale("en")}
          >
            EN
          </button>
          <button
            type="button"
            className="sq-langtoggle__btn"
            aria-pressed={locale === "ar"}
            onClick={() => setLocale("ar")}
          >
            AR
          </button>
        </div>
        {/* D-W3: signed-in and signed-out headers used to differ only by a
            link label. /legacy shows an identity chip -- round avatar with
            initials, first name, plan badge -- and swaps the sign-in and
            subscribe affordances away (EjarMasr.dc.html:65-71). Transcribed.
            The chip leads to the panel, whose sidebar carries sign-out. */}
        {profile ? (
          /* D-W9: the chip used to navigate straight to the panel, which left
             sign-out reachable only from inside the panel. It opens a menu
             now; see UserMenu. */
          <UserMenu />
        ) : (
          <>
            <Link to="/login" className="sq-header__signin">{t.nav.signIn}</Link>
            {/* The plans are paid by people who advertise, so the header
                carries their door too (amendment 1).

                Signed in it goes straight to listing creation. Signed out it
                uses the D-W1 contract -- setReturnPath then /login -- NOT a
                ?next= query, which Login does not read. A link that looks
                like it returns you and does not is the fake affordance this
                project keeps finding. */}
            {profile ? (
              <Link className="sq-header__list" to="/account#listings">{t.home.listCta}</Link>
            ) : (
              <button type="button" className="sq-header__list"
                onClick={() => goToLoginAndReturn(navigate, "/account#listings")}>
                {t.home.listCta}
              </button>
            )}
            <Link className="sq-header__subscribe" to="/pricing">{t.nav.subscribe}</Link>
          </>
        )}
      </div>
    </header>
  );
}
